import { getCapitalGlobalProposalAttachmentDisplayFileName } from '@/features/proposals/components/capital-global/utils/capital-global-proposal-attachment.util';
import {
  mapAttachmentsByDocumentType,
  useCapitalGlobalProposalAttachments,
} from '@/features/proposals/hooks/capital-global/use-capital-global-proposal-attachments.hook';
import type { PolicyDetailsResponse } from '@/services/policies/types';
import type { CapitalGlobalProposalAttachment } from '@/services/proposals/types';
import { toaster } from '@/shared/ui/toaster.component';
import { downloadFromSignedUrl } from '@/shared/utils/signed-url.util';
import { Button, Text } from '@noorden-insurtech/alba-design-system';
import { ChevronDown, ChevronUp, Eye } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { AccordionSection, InfoRow, MobileLabeledField } from '../policy-section.shared';
import { CapitalGlobalPolicyDocumentModal } from './capital-global-policy-document-modal.component';
import { CapitalGlobalPolicyPremiumCard } from './capital-global-policy-premium-card.component';
import { CapitalGlobalStipulantCard } from './capital-global-stipulant-card.component';
import { mapPolicyDetailsToCapitalGlobalView } from './map-policy-details-to-capital-global-view.util';
import type { BrokerWriteGuard } from '../../../brokers/utils/broker-status.util';

type Props = {
  data: PolicyDetailsResponse;
  productName: string;
  statusLabel: string;
  /** Trava de corretora inativa dos documentos da apólice (mesma da PolicyTab). */
  downloadGuard?: BrokerWriteGuard;
};

type DocumentModalState = {
  title: string;
  attachment: CapitalGlobalProposalAttachment;
} | null;

export function CapitalGlobalPolicyTab({ data, productName, statusLabel, downloadGuard }: Props) {
  const [openStipulants, setOpenStipulants] = useState<Set<number>>(new Set());
  const [documentModal, setDocumentModal] = useState<DocumentModalState>(null);

  const view = useMemo(
    () =>
      mapPolicyDetailsToCapitalGlobalView(data, {
        productName,
        statusLabel,
      }),
    [data, productName, statusLabel],
  );

  const { premium, emission, stipulantHeader, stipulants, commercialization } = view;

  const trimmedPolicyId = (data.policyId ?? '').trim();
  const attachmentsQuery = useCapitalGlobalProposalAttachments(trimmedPolicyId);
  const attachmentsByType = useMemo(
    () => mapAttachmentsByDocumentType(attachmentsQuery.data ?? []),
    [attachmentsQuery.data],
  );

  const handleDownloadAttachment = useCallback(
    async (attachment?: CapitalGlobalProposalAttachment) => {
      if (!attachment?.downloadUrl) {
        toaster.create({
          type: 'error',
          title: 'Download indisponível',
          description: 'Não foi possível obter o link de download.',
          duration: 5000,
        });
        return;
      }

      try {
        await downloadFromSignedUrl(
          attachment.downloadUrl,
          getCapitalGlobalProposalAttachmentDisplayFileName(
            attachment.fileName,
            attachment.keyPath,
          ),
        );
      } catch {
        toaster.create({
          type: 'error',
          title: 'Erro ao baixar arquivo',
          description: 'Não foi possível baixar o documento.',
          duration: 5000,
        });
      }
    },
    [],
  );

  const handleOpenDocumentModal = useCallback(
    (title: string, attachment?: CapitalGlobalProposalAttachment) => {
      if (!attachment) return;
      setDocumentModal({ title, attachment });
    },
    [],
  );

  const handleCloseDocumentModal = useCallback(() => setDocumentModal(null), []);

  const handleConfirmDownload = useCallback(() => {
    if (documentModal) handleDownloadAttachment(documentModal.attachment);
    setDocumentModal(null);
  }, [documentModal, handleDownloadAttachment]);

  const allOpen =
    stipulants.length > 0 && stipulants.every((_, index) => openStipulants.has(index));

  const toggleStipulant = (index: number) => {
    setOpenStipulants(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleAll = () => {
    setOpenStipulants(
      allOpen ? new Set() : new Set(stipulants.map((_, index) => index)),
    );
  };

  return (
    <div className='flex flex-col gap-2'>
      <div className='mb-4'>
        <CapitalGlobalPolicyPremiumCard data={premium} />
      </div>

      {/* Dados da Emissão */}
      <AccordionSection title='Dados da Emissão'>
        <div className='hidden sm:grid sm:grid-cols-4 sm:gap-6'>
          <InfoRow label='Início de Vigência' value={emission.effectiveDate} />
          <InfoRow label='Fim de Vigência' value={emission.expiryDate} />
          <InfoRow label='Data da Emissão' value={emission.issueDate} />
          <InfoRow label='Tipo de Seguro' value={emission.insuranceType} />
        </div>
        <div className='grid grid-cols-2 gap-x-4 gap-y-4 sm:hidden'>
          <MobileLabeledField label='Início de Vigência' value={emission.effectiveDate} showCalendar withDivider={false} />
          <MobileLabeledField label='Fim de Vigência' value={emission.expiryDate} showCalendar withDivider={false} />
          <MobileLabeledField label='Data da Emissão' value={emission.issueDate} showCalendar withDivider={false} />
          <MobileLabeledField label='Tipo de Seguro' value={emission.insuranceType} withDivider={false} />
        </div>
      </AccordionSection>

      {/* Dados do Estipulante */}
      <AccordionSection title='Dados do Estipulante'>
        <div className='hidden sm:grid sm:grid-cols-4 sm:gap-6'>
          <InfoRow label={stipulantHeader.documentLabel} value={stipulantHeader.documentValue} />
          <InfoRow label='Razão Social' value={stipulantHeader.socialName} />
          <InfoRow label='E-mail' value={stipulantHeader.email} />
          <InfoRow label='Telefone' value={stipulantHeader.phone} />
        </div>
        <div className='flex flex-col gap-2 sm:hidden'>
          <MobileLabeledField label={stipulantHeader.documentLabel} value={stipulantHeader.documentValue} />
          <MobileLabeledField label='Razão Social' value={stipulantHeader.socialName} />
          <MobileLabeledField label='E-mail' value={stipulantHeader.email} />
          <MobileLabeledField label='Telefone' value={stipulantHeader.phone} withDivider={false} />
        </div>
      </AccordionSection>

      {/* Dados do Risco — Lista de Estipulantes */}
      <AccordionSection title='Dados do Risco'>
        <div className='flex flex-col items-start gap-4 self-stretch'>
          <div className='flex w-full items-center justify-between'>
            <Text className='text-base text-alba-black-500'>Lista de Estipulantes</Text>
            {stipulants.length > 0 && (
              <button
                onClick={toggleAll}
                className='flex items-center gap-2 rounded-lg px-4 py-2 text-alba-blue-500 transition-colors hover:bg-alba-gray-100'
              >
                <span className='text-xs font-medium'>
                  {allOpen ? 'Fechar Todos' : `Abrir Todos (${stipulants.length})`}
                </span>
                {allOpen ? (
                  <ChevronUp className='h-4 w-4' strokeWidth={1.6} />
                ) : (
                  <ChevronDown className='h-4 w-4' strokeWidth={1.6} />
                )}
              </button>
            )}
          </div>
          <div className='flex w-full flex-col gap-2'>
            {stipulants.map((entry, index) => (
              <CapitalGlobalStipulantCard
                key={entry.label}
                entry={entry}
                isOpen={openStipulants.has(index)}
                onToggle={() => toggleStipulant(index)}
              />
            ))}
          </div>
        </div>
      </AccordionSection>

      {/* Comercialização */}
      <AccordionSection title='Comercialização'>
        <div className='flex flex-col gap-4'>
          <span className='text-sm font-bold text-alba-black-500'>Dados do Corretor</span>
          <div className='hidden sm:grid sm:grid-cols-2 sm:gap-6'>
            <InfoRow label='Corretor' value={data.agents?.[0]?.name} />
            <InfoRow label='Percentual de Corretagem' value={commercialization.brokeragePercent} />
            <InfoRow label='Percentual de Agenciamento' value={commercialization.agencingPercent} />
          </div>
          <div className='flex flex-col gap-2 sm:hidden'>
            <MobileLabeledField label='Corretor' value={data.agents?.[0]?.name} />
            <MobileLabeledField label='Percentual de Corretagem' value={commercialization.brokeragePercent} />
            <MobileLabeledField label='Percentual de Agenciamento' value={commercialization.agencingPercent} withDivider={false} />
          </div>
        </div>
      </AccordionSection>

      {/* Documentação */}
      <AccordionSection title='Documentação'>
        <div className='flex flex-col gap-3 sm:flex-row'>
          <Button
            variant='secondary'
            size='sm'
            disabled={
              attachmentsQuery.isLoading ||
              !attachmentsByType.signed_proposal ||
              downloadGuard?.blocked
            }
            title={downloadGuard?.reason}
            onClick={() =>
              handleOpenDocumentModal('Proposta Assinada', attachmentsByType.signed_proposal)
            }
            className='w-full bg-alba-green-500 text-alba-white-500 [&>svg]:text-alba-white-500 sm:w-auto'
          >
            <Eye className='mr-2 h-4 w-4' />
            Proposta Assinada
          </Button>
          <Button
            variant='secondary'
            size='sm'
            disabled={
              attachmentsQuery.isLoading || !attachmentsByType.gfip || downloadGuard?.blocked
            }
            title={downloadGuard?.reason}
            onClick={() => handleOpenDocumentModal('GFIP', attachmentsByType.gfip)}
            className='w-full bg-alba-green-500 text-alba-white-500 [&>svg]:text-alba-white-500 sm:w-auto'
          >
            <Eye className='mr-2 h-4 w-4' />
            GFIP
          </Button>
        </div>
      </AccordionSection>

      <CapitalGlobalPolicyDocumentModal
        isOpen={Boolean(documentModal)}
        title={documentModal?.title ?? ''}
        fileName={
          documentModal
            ? getCapitalGlobalProposalAttachmentDisplayFileName(
                documentModal.attachment.fileName,
                documentModal.attachment.keyPath,
              )
            : undefined
        }
        downloadUrl={documentModal?.attachment.downloadUrl}
        onClose={handleCloseDocumentModal}
        onDownload={handleConfirmDownload}
      />
    </div>
  );
}
